import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Badges from '../Badges/Badges';
import H from '../H/H';
import Icon from '../Icon/Icon';

/**
 * `Header` is a container typically used as header for lists and similar components.
 *
 * It has support for additional floating buttons, a popup menu, state and badge indicators, a toned down background image
 * and custom components, such as {@link Breadcrumbs}.
 *
 * @property {string} backgroundUrl The URL to the background image. Defaults to no background image.
 * @property {node} badges One or more badges that will be displayed in the upper left corner of the header. It is
 * recommended to use *badgeNames* to provide icons bundled with the vimond-core-components.
 * @property {array} badgeNames An array of icon names supported by the {@link Icon} component. Defaults to no badges.
 * @property {any} children Child components will be placed inside a toolbar. The toolbar will not be created if no children
 * are provided.
 * @property {string} className The class name of this component. Will be appended to "panel-header".
 * @property {number} level The level of the title. Defaults to 2 (`h2`).
 * @property {node} menu If present, the popup menu will be added to this header. No popup menu is displayed by default.
 * @property {function} onAdd A callback which will be called when the **Add** button is clicked. The button will only be
 * created when this callback is present.
 * @property {function} onClose A callback which will be called when the close button is clicked. The button will only be
 * created when this callback is present.
 * @property {function} onPublish A callback which will be called when the **Publish** button is clicked. The button will
 * only be created when this callback is present.
 * @property {string} tag The state tag want to display before the icons. This should be the state of your item such
 * as "Draft", "Modified", etc.
 * @property {string} title The title of this header. Defaults to a `h2` element. Use the *level* prop to create a `h1`,
 * `h3`, `h4`, `h5` or `h6` title element.
 *
 * @see List, PopupMenu
 */
export default class Header extends PureComponent {

  static displayName = 'Header';

  static propTypes = {
    backgroundUrl: PropTypes.string,
    badges: PropTypes.node,
    badgeNames: PropTypes.arrayOf(PropTypes.string),
    children: PropTypes.node,
    className: PropTypes.string,
    level: PropTypes.number,
    menu: PropTypes.node,
    onAdd: PropTypes.func,
    onClose: PropTypes.func,
    onPublish: PropTypes.func,
    tag: PropTypes.string,
    title: PropTypes.string
  };

  static defaultProps = {
    level: 2
  };

  render() {
    const { backgroundUrl, badges, badgeNames, className, children, level, menu, onAdd, onClose, onPublish, tag, title, ...rest } = this.props;
    let toolbar;
    let bgStyle;
    let controls;

    if (backgroundUrl) {
      bgStyle = {
        backgroundImage: `url('${backgroundUrl}')`
      };
    }
    if (onClose) {
      controls = (
        <span className="controls">
          {onClose && <button className="close" onClick={onClose}><Icon name="close"/></button>}
        </span>
      );
    }
    if (children) {
      toolbar = (
        <div className="toolbar">
          {children}
        </div>
      );
    }
    return (
      <header {...rest} className={cx('core-header', className)}>
        <div className="background-image" style={bgStyle}/>
        <div className="panel-header-inner-wrapper">
          {(tag || badges || badgeNames) && <Badges label={tag} iconNames={badgeNames}>{badges}</Badges>}
          {controls}
          <div className="title-wrapper">
            {title && <H level={level}>{title}</H>}
            {menu}
          </div>
          {toolbar}
          {onAdd && <button className="floating-button" onClick={onAdd}><Icon name="add"/></button>}
          {onPublish && <button className="floating-button" onClick={onPublish}><Icon name="publish"/></button>}
        </div>
      </header>
    );
  }

}
